"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "@/components/icons/Icons";
import FloatingInput from "@/components/ui/FloatingInputField";
import SixDigitOtpInput from "@/components/ui/SixDigitOtpInput";
import { useAuthForm } from "@/utils/useAuthForm";
import Link from "next/link";

interface LoginPageFormProps {
  onSuccess?: () => void;
  customRedirect?: string;
  showBrandingSide?: boolean;
}

const CAROUSEL_SLIDES = [
  {
    image: "/next_assets/images/common-images/login-page-img-1.png",
    tag: "FREEDOM WITH STABILITY",
    title: "Free Portfolio Analysis",
    description:
      "Analyze your portfolio to secure steady income, grow your wealth, and plan for early retirement.",
    alt: "Free Portfolio Analysis",
  },
  {
    image: "/next_assets/images/common-images/login-page-img-2.png",
    tag: "FINANCIAL FREEDOM",
    title: "Plan early to live life on your own terms.",
    description:
      "Start your SIP journey today to achieve lifelong peace of mind and freedom.",
    alt: "Enjoying financial freedom on the beach",
  },
  {
    image: "/next_assets/images/common-images/login-page-img-3.png",
    tag: "WEALTH CREATION",
    title: "Small consistent steps build big fortunes.",
    description:
      "Harness the power of compounding with curated mutual fund portfolios.",
    alt: "Plant growing out of rock representing wealth creation",
  },
];

export default function LoginPageForm({
  onSuccess,
  customRedirect,
  showBrandingSide = true,
}: LoginPageFormProps) {
  const pathname = usePathname();

  // Carousel current slide
  const [currentSlide, setCurrentSlide] = useState(0);

  // Modular auth hook encapsulating all handlers, steps, timers, and server actions
  const {
    step,
    setStep,
    inputMode,
    setInputMode,
    whatsappConsent,
    setWhatsappConsent,
    phone,
    email,
    name,
    password,
    otp,
    isOtpVerifying,
    resendTimer,
    canResend,
    otpSuccessMessage,
    forgotEmail,
    setForgotEmail,
    forgotPasswordSuccessMessage,
    googleData,
    loading,
    errors,
    setErrors,
    isLoggedIn,
    handlePhoneChange,
    handleEmailChange,
    handleNameChange,
    handlePasswordChange,
    handleOtpChange,
    handleForgotEmailChange,
    handleResendOtp,
    handleGoogleBtn,
    handleBack,
    handleStepSubmit,
    getSubmitButtonText,
  } = useAuthForm({
    onSuccess,
    customRedirect,
    leadSource: pathname,
    buttonType: "login_page",
  });

  // Carousel auto-advance every 15 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % CAROUSEL_SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Redirect to customRedirect or ROOT / if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      window.location.href = customRedirect || "/";
    }
  }, [isLoggedIn, customRedirect]);

  return (
    <div
      className="w-full max-w-screen-lg mx-auto bg-white rounded-4xl sm:rounded-8xl p-4 flex gap-6 lg:gap-8"
      style={{ boxShadow: "0px 16px 32px 0px #0F172A26" }}
    >
      {/* LEFT COLUMN: Image Carousel with Glassmorphism Overlay */}
      {showBrandingSide && (
        <div className="hidden lg:flex relative shrink-0 rounded-4xl sm:rounded-6xl overflow-hidden bg-[#070e1b] w-full max-w-500 h-162 flex-col justify-between p-5 sm:p-6 select-none shadow-xs">
          {/* Slide Image with Smooth Cross-Fade */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full"
            >
              <Image
                src={CAROUSEL_SLIDES[currentSlide].image}
                alt={CAROUSEL_SLIDES[currentSlide].alt}
                width={550}
                height={800}
                priority={currentSlide === 0}
                className="object-contain object-center"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </motion.div>
          </AnimatePresence>

          <div className="absolute inset-0 bg-linear-to-b/srgb from-transparent via-[#070e1b]/40 to-[#070e1b]/80 z-0 pointer-events-none" />

          {/* Frosted Glass Overlay Card & Carousel Indicators at Bottom */}
          <div className="relative z-1 mt-auto">
            <div className="bg-[#0b1626]/80 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/10 text-white shadow-xl">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-2 h-2 rounded-full bg-[#00d084] inline-block shadow-[0_0_8px_#00d084]" />
                <span className="text-[10px] sm:text-[11px] font-bold tracking-wider text-[#00d084] uppercase">
                  {CAROUSEL_SLIDES[currentSlide].tag}
                </span>
              </div>
              <div className="text-lg sm:text-xl sm:leading-7 font-bold italic tracking-tight leading-snug text-white">
                {CAROUSEL_SLIDES[currentSlide].title}
              </div>
              <p className="text-xs text-white/80 mt-1 leading-relaxed">
                {CAROUSEL_SLIDES[currentSlide].description}
              </p>
            </div>

            {/* Carousel Indicators */}
            <div className="flex items-center justify-center gap-1.5 mt-3.5">
              {CAROUSEL_SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    currentSlide === idx
                      ? "w-6 h-1.5 bg-white"
                      : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* RIGHT COLUMN: Form Card */}
      <div className="w-full max-w-500 lg:max-w-none mx-auto flex flex-col justify-center xs:py-4 xs:px-2">
        {/* Header: Centered Logo and Welcome Title */}
        <div className="text-center mb-5 sm:mb-6">
          <Link
            href="/"
            prefetch={true}
            aria-label="MySIPonline Logo"
            className="flex justify-center mb-4.5"
          >
            <Icon.headerNewLogoIcon className="w-full max-w-200 text-primaryBlue" />
          </Link>

          <h1 className="text-2xl sm:text-3.5xl font-bold text-primaryBlack tracking-[-3%]">
            {step === "identifier" && "Welcome"}
            {step === "signin" && "Welcome back"}
            {step === "signup_details" && "Create account"}
            {step === "signup_otp" && "Verify number"}
            {step === "google_phone" && "One last step"}
            {step === "google_otp" && "Verify number"}
            {step === "forgot_password" && "Forgot password"}
            {step === "forgot_password_success" && "Check your email"}
          </h1>
        </div>

        <form onSubmit={handleStepSubmit}>
          <div className="xs:rounded-6xl xs:border border-[#CECECE] xs:p-5">
            {/* STEP 1: Phone / Email Identifier */}
            {step === "identifier" && (
              <div className="space-y-3.5">
                {/* Phone Input with Indian Flag */}
                {inputMode === "phone" ? (
                  <div>
                    <div
                      className={`relative flex items-center gap-2 rounded-2lg sm:rounded-xl border transition bg-white ${
                        errors.phone
                          ? "border-red-500"
                          : "border-primaryBlue/80 focus-within:border-primaryBlue"
                      }`}
                    >
                      <div className="shrink-0 flex items-center border-r border-primaryBlue/25 px-2.5 py-2">
                        <Icon.indianflagIcon className="shrink-0 w-8 h-7" />
                      </div>
                      <span className="text-primaryBlack/50 font-semibold text-base select-none shrink-0">
                        +91 -
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        value={phone}
                        autoFocus
                        disabled={loading}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="0000000000"
                        className="w-full font-semibold text-primaryBlack text-base sm:text-lg tracking-wide placeholder:font-normal placeholder:text-primaryBlack/50 focus:outline-hidden bg-transparent"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-xs text-red-500 mt-1 font-medium ml-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <div
                      className={`relative flex items-center gap-2 rounded-2lg sm:rounded-xl px-4 py-2.5 border transition bg-white ${
                        errors.email
                          ? "border-red-500"
                          : "border-primaryBlue/80 focus-within:border-primaryBlue"
                      }`}
                    >
                      <input
                        type="email"
                        value={email}
                        autoFocus
                        disabled={loading}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        placeholder="Enter email address"
                        className="w-full font-medium text-primaryBlack/90 text-2sm leading-1.6 tracking-wide placeholder:font-normal placeholder:text-primaryBlack/50 focus:outline-hidden bg-transparent"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1 font-medium ml-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                )}

                {/* WhatsApp Updates Checkbox */}
                <label className="flex items-center gap-1.5 select-none cursor-pointer group text-xs text-slate-700 font-medium">
                  <input
                    type="checkbox"
                    checked={whatsappConsent}
                    onChange={(e) => setWhatsappConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={[
                      "w-4 h-4 rounded-sm flex items-center justify-center transition",
                      whatsappConsent
                        ? "bg-[#00BA4B] text-white"
                        : "border-2 border-slate-300 bg-white group-hover:border-slate-400",
                    ].join(" ")}
                  >
                    {whatsappConsent && (
                      <Icon.rightSignIcon className="w-2.5 h-2.5 text-white" />
                    )}
                  </div>
                  Get investment updates on
                  <span className="inline-flex items-center gap-0.5 text-[#1F840D] font-medium">
                    <Icon.whatsappIconV4 className="w-4.5 h-4.5 inline text-[#1F840D]" />
                    Whatsapp
                  </span>
                </label>

                {/* Divider "or" */}
                <div className="relative my-4 text-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-black/30" />
                  </div>
                  <span className="relative bg-white px-2 text-sm text-black/50 font-medium">
                    or
                  </span>
                </div>

                {/* Social Login Buttons */}
                <div className="space-y-2.5">
                  {/* Google Login */}
                  <button
                    type="button"
                    onClick={handleGoogleBtn}
                    disabled={loading}
                    className="w-full py-2.5 px-5 rounded-full border border-primaryBlue/20 bg-[#F0F6FF] hover:bg-[#e4eeff] text-primaryBlack font-medium text-sm sm:text-base flex items-center justify-center gap-2 transition disabled:opacity-60"
                  >
                    <Icon.googleIcon className="w-4 h-4 shrink-0" />
                    <span>Continue with Google</span>
                  </button>

                  {/* Mail / Mobile Toggle Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setInputMode(inputMode === "phone" ? "email" : "phone");
                      setErrors({});
                    }}
                    disabled={loading}
                    className="w-full py-2.5 px-5 rounded-full border border-primaryBlue/20 bg-[#F0F6FF] hover:bg-[#e4eeff] text-primaryBlack font-medium text-sm sm:text-base flex items-center justify-center gap-2 transition disabled:opacity-60"
                  >
                    {inputMode === "phone" ? (
                      <>
                        <Icon.emailIconV2 />
                        <span>Continue with Mail</span>
                      </>
                    ) : (
                      <>
                        <Icon.callIconV3 className="w-4 h-4 shrink-0" />
                        <span>Continue with Mobile Number</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Sign In (User Exists) */}
            {step === "signin" && (
              <div className="space-y-4">
                {inputMode === "email" ? (
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-primaryBlack/90 font-medium">
                        Account Password
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setForgotEmail(email);
                          setErrors({});
                          setStep("forgot_password");
                        }}
                        className="text-xs font-semibold text-[#1453b3] hover:underline cursor-pointer"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <FloatingInput
                      type="password"
                      label="Enter Password"
                      value={password}
                      autoFocus
                      isDisabled={loading}
                      onChange={handlePasswordChange}
                      error={errors.password}
                      showFloatingLabel={false}
                    />
                  </div>
                ) : (
                  <div>
                    <SixDigitOtpInput
                      label="Enter 6-Digit OTP"
                      value={otp}
                      autoFocus
                      isDisabled={loading || isOtpVerifying}
                      onChange={handleOtpChange}
                      error={errors.otp}
                    />
                    {otpSuccessMessage && !errors.otp && (
                      <p className="text-2xs text-emerald-600 font-medium mt-2 flex items-center gap-1">
                        <svg
                          className="w-3.5 h-3.5 inline shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {otpSuccessMessage}
                      </p>
                    )}
                    <div className="flex justify-between items-center text-xs mt-2 px-1">
                      <span className="text-slate-500">
                        Didn&apos;t receive OTP?
                      </span>
                      {canResend ? (
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={loading || isOtpVerifying}
                          className="font-semibold text-primaryBlue hover:underline cursor-pointer disabled:opacity-50"
                        >
                          Resend OTP
                        </button>
                      ) : (
                        <span className="text-slate-400 font-medium">
                          Resend in{" "}
                          <span className="font-semibold text-slate-600">
                            {resendTimer}s
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3A: Sign Up Details (User Does Not Exist) */}
            {step === "signup_details" && (
              <div className="space-y-3.5">
                <FloatingInput
                  type="text"
                  label="Full Name"
                  value={name}
                  autoFocus
                  isDisabled={loading}
                  onChange={handleNameChange}
                  error={errors.name}
                  showFloatingLabel={false}
                />

                {inputMode === "email" ? (
                  <>
                    <FloatingInput
                      type="email"
                      label="Email Address"
                      value={email}
                      isDisabled={true}
                      onChange={handleEmailChange}
                      error={errors.email}
                      showFloatingLabel={false}
                    />
                    <FloatingInput
                      type="tel"
                      label="Mobile Number"
                      value={phone}
                      maxLength={10}
                      isDisabled={loading}
                      onChange={handlePhoneChange}
                      error={errors.phone}
                      showFloatingLabel={false}
                    />
                  </>
                ) : (
                  <>
                    <FloatingInput
                      type="tel"
                      label="Mobile Number"
                      value={phone}
                      maxLength={10}
                      isDisabled={true}
                      onChange={handlePhoneChange}
                      error={errors.phone}
                      showFloatingLabel={false}
                    />
                    <FloatingInput
                      type="email"
                      label="Email Address"
                      value={email}
                      isDisabled={loading}
                      onChange={handleEmailChange}
                      error={errors.email}
                      showFloatingLabel={false}
                    />
                  </>
                )}

                <FloatingInput
                  type="password"
                  label="Create Password"
                  value={password}
                  isDisabled={loading}
                  onChange={handlePasswordChange}
                  error={errors.password}
                  showFloatingLabel={false}
                />
              </div>
            )}

            {/* STEP 3B: Verify OTP after entering details */}
            {step === "signup_otp" && (
              <div className="space-y-4">
                <div>
                  <SixDigitOtpInput
                    label="Enter 6-Digit Verification Code"
                    value={otp}
                    autoFocus
                    isDisabled={loading || isOtpVerifying}
                    onChange={handleOtpChange}
                    error={errors.otp}
                  />
                  {otpSuccessMessage && !errors.otp && (
                    <p className="text-2xs text-emerald-600 font-medium mt-2 flex items-center gap-1">
                      <svg
                        className="w-3.5 h-3.5 inline shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {otpSuccessMessage}
                    </p>
                  )}
                  <div className="flex justify-between items-center text-xs mt-2 px-1">
                    <span className="text-slate-500">
                      Didn&apos;t receive OTP?
                    </span>
                    {canResend ? (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading || isOtpVerifying}
                        className="font-semibold text-[#1453b3] hover:underline cursor-pointer disabled:opacity-50"
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <span className="text-slate-400 font-medium">
                        Resend in{" "}
                        <span className="font-semibold text-slate-600">
                          {resendTimer}s
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4A: Google Phone Required */}
            {step === "google_phone" && (
              <div className="space-y-3.5">
                <div className="p-3 rounded-2xl bg-blue-50/70 border border-blue-100 flex items-center gap-3">
                  <Icon.googleIcon className="w-5 h-5 shrink-0" />
                  <div className="text-xs text-blue-900 leading-tight">
                    <span className="font-semibold block">
                      {googleData.name}
                    </span>
                    <span className="text-blue-700">{googleData.email}</span>
                  </div>
                </div>

                <div
                  className={`relative flex items-center rounded-2xl border transition bg-white px-3.5 py-3 ${
                    errors.phone
                      ? "border-red-500"
                      : "border-blue-300 focus-within:border-blue-600"
                  }`}
                >
                  <div className="shrink-0 flex items-center">
                    <Icon.indianflagIcon className="w-6 h-6 rounded-xs" />
                  </div>
                  <span className="text-slate-500 font-semibold text-base select-none ml-2.5 mr-1.5">
                    +91-
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    value={phone}
                    autoFocus
                    disabled={loading}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="Enter mobile number"
                    className="w-full font-bold text-slate-900 text-base sm:text-lg tracking-wide placeholder:font-normal placeholder:text-slate-300 focus:outline-hidden bg-transparent"
                  />
                </div>
                {errors.phone && (
                  <p className="text-xs text-red-500 mt-1 font-medium ml-1">
                    {errors.phone}
                  </p>
                )}
              </div>
            )}

            {/* STEP 4B: Google OTP Verification */}
            {step === "google_otp" && (
              <div className="space-y-4">
                <div>
                  <SixDigitOtpInput
                    label="Enter 6-Digit Verification Code"
                    value={otp}
                    autoFocus
                    isDisabled={loading || isOtpVerifying}
                    onChange={handleOtpChange}
                    error={errors.otp}
                  />
                  {otpSuccessMessage && !errors.otp && (
                    <p className="text-2xs text-emerald-600 font-medium mt-2 flex items-center gap-1">
                      <svg
                        className="w-3.5 h-3.5 inline shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {otpSuccessMessage}
                    </p>
                  )}
                  <div className="flex justify-between items-center text-xs mt-2 px-1">
                    <span className="text-slate-500">
                      Didn&apos;t receive OTP?
                    </span>
                    {canResend ? (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading || isOtpVerifying}
                        className="font-semibold text-[#1453b3] hover:underline cursor-pointer disabled:opacity-50"
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <span className="text-slate-400 font-medium">
                        Resend in{" "}
                        <span className="font-semibold text-slate-600">
                          {resendTimer}s
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 5: Forgot Password Start */}
            {step === "forgot_password" && (
              <div>
                <p className="text-xs text-primaryBlack/90 font-medium mb-2">
                  Enter Registered Email
                </p>
                <FloatingInput
                  type="email"
                  label="Enter Registered Email"
                  value={forgotEmail}
                  autoFocus
                  isDisabled={loading}
                  onChange={handleForgotEmailChange}
                  error={errors.forgotEmail}
                  showFloatingLabel={false}
                  mbClass={false}
                />
                <p className="text-xs text-primaryBlack/60 mt-5">
                  We will send you a password reset link to this email.
                </p>
              </div>
            )}

            {/* STEP 5B: Forgot Password Link Sent Success */}
            {step === "forgot_password_success" && (
              <div className="py-2 text-center space-y-3.5">
                <div className="w-full max-w-fit mx-auto p-2.5 rounded-full bg-primaryGreen/10 border border-primaryGreen/80 flex items-center justify-center text-emerald-600 shadow-xs">
                  <Icon.emailIconV4 className="w-6 h-6" />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm sm:text-base sm:leading-6 font-semibold text-primaryBlack leading-snug">
                    {forgotPasswordSuccessMessage ||
                      "We have sent you a password reset link on your email"}
                  </p>
                  {forgotEmail && (
                    <p className="text-xs font-semibold text-primaryBlue break-all bg-primaryBlue/5 py-1 px-3 rounded-full inline-block">
                      {forgotEmail}
                    </p>
                  )}
                  <p className="text-xs text-primaryBlack/60 pt-1 leading-relaxed max-w-xs mx-auto">
                    Please check your inbox or spam folder and click the link to
                    reset your password.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Terms & Privacy Disclaimer - Placed ABOVE the CTA Button matching screenshot */}
          {step === "identifier" && (
            <p className="text-[11px] text-primaryBlack text-center leading-relaxed mt-4">
              By Providing your{" "}
              {inputMode === "phone" ? "Mobile Number" : "Email"} you agree to
              Our{" "}
              <Link
                href="/terms-conditions"
                target="_blank"
                rel="noreferrer"
                className="underline text-primaryBlue transition hover:text-[#001331]"
              >
                T&C
              </Link>{" "}
              and{" "}
              <Link
                href="/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="underline text-primaryBlue transition hover:text-[#001331]"
              >
                Privacy Policy
              </Link>
            </p>
          )}
          {/* CTA Buttons Row: Animated Secondary Back Button + Main Submit CTA */}
          <div className="mt-6 flex items-center">
            <AnimatePresence initial={false}>
              {step !== "identifier" && (
                <motion.div
                  key="back-button-wrapper"
                  initial={{ width: 0, opacity: 0, marginRight: 0 }}
                  animate={{ width: "auto", opacity: 1, marginRight: 12 }}
                  exit={{ width: 0, opacity: 0, marginRight: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden shrink-0"
                >
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={loading || isOtpVerifying}
                    className="group flex items-center justify-center gap-1.5 w-24 sm:w-28 py-3 px-3 rounded-full border border-primaryBlue/30 bg-[#F0F6FF] hover:bg-[#e4eeff] hover:border-primaryBlue/60 active:scale-[0.98] text-primaryBlue font-bold text-sm sm:text-base transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap shadow-xs"
                  >
                    <Icon.rightArrow className="rotate-180 shrink-0" />
                    Back
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading || isOtpVerifying}
              className="flex-1 rounded-full py-3 px-4 bg-primaryBlue hover:bg-blue-800 active:scale-[0.98] text-white font-bold text-sm sm:text-base shadow-xs transition flex items-center justify-center cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed text-center whitespace-nowrap"
            >
              {loading || isOtpVerifying ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                getSubmitButtonText()
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
